import React, { useState, useEffect } from 'react';
import { FaTimes, FaRupeeSign, FaCheckCircle, FaClock, FaExclamationTriangle, FaEnvelope, FaCalendarAlt, FaCreditCard } from 'react-icons/fa';
import HttpInterceptor from '../lib/HttpInterceptor';
import { toast } from 'react-toastify';
import { formatPaymentStatus, getPaymentStatusColor } from '../utils/paymentStatusFormatter';
import { formatCurrency } from '../utils/currencyFormatter';
import { formatDateLocale } from '../utils/dateFormatter';

/**
 * Payment History Modal Component
 * Displays payment timeline and transaction history for a student
 */
function PaymentHistoryModal({ isOpen, onClose, studentId, studentName }) {
  const [paymentHistory, setPaymentHistory] = useState(null);
  const [reminderHistory, setReminderHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSendingReminder, setIsSendingReminder] = useState(false);

  useEffect(() => {
    if (isOpen && studentId) {
      fetchPaymentHistory();
      fetchReminderHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, studentId]);

  const fetchPaymentHistory = async () => {
    try {
      setIsLoading(true);
      const response = await HttpInterceptor.get(`/api/fee-plan/students/${studentId}/payment-summary`);
      setPaymentHistory(response.data);
    } catch (error) {
      console.error('Error fetching payment history:', error);
      toast.error('Failed to load payment history');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchReminderHistory = async () => {
    try {
      const response = await HttpInterceptor.get(`/api/fee-plan/students/${studentId}/reminder-history`);
      setReminderHistory(response.data?.history || []);
    } catch (error) {
      console.error('Error fetching reminder history:', error);
      // Don't show error toast for reminder history as it's optional
    }
  };

  const handleSendReminder = async () => {
    try {
      setIsSendingReminder(true);
      await HttpInterceptor.post('/api/fee-plan/reminders/send', {
        studentId: studentId
      });
      toast.success('Payment reminder sent successfully');
      fetchReminderHistory();
    } catch (error) {
      console.error('Error sending reminder:', error);
      toast.error(error.response?.data?.message || 'Failed to send reminder');
    } finally {
      setIsSendingReminder(false);
    }
  };

  if (!isOpen) return null;

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <FaCheckCircle className="text-green-600" />;
      case 'pending':
        return <FaClock className="text-yellow-600" />;
      case 'overdue':
        return <FaExclamationTriangle className="text-red-600" />;
      default:
        return <FaClock className="text-gray-600" />;
    }
  };


  const transactions = paymentHistory?.transactions || [];
  const summary = paymentHistory?.summary || {};

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Payment History</h2>
            <p className="text-sm text-gray-600 mt-1">{studentName}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-md p-2"
            aria-label="Close modal"
          >
            <FaTimes className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading payment history...</p>
            </div>
          ) : (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-green-600 font-medium">Total Paid</p>
                      <p className="text-2xl font-bold text-green-700 mt-1">
                        {formatCurrency(summary.totalPaid || 0)}
                      </p>
                    </div>
                    <FaCheckCircle className="text-green-600 text-2xl" />
                  </div>
                </div>
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-yellow-600 font-medium">Pending</p>
                      <p className="text-2xl font-bold text-yellow-700 mt-1 flex items-center">
                        <FaRupeeSign className="mr-1" />
                        {summary.totalPending || 0}
                      </p>
                    </div>
                    <FaClock className="text-yellow-600 text-2xl" />
                  </div>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-blue-600 font-medium">Due Amount</p>
                      <p className="text-2xl font-bold text-blue-700 mt-1">
                        {formatCurrency(summary.amountDue || 0)}
                      </p>
                    </div>
                    <FaCreditCard className="text-blue-600 text-2xl" />
                  </div>
                </div>
              </div>

              {/* Send Reminder Button */}
              {(summary.paymentStatus === 'pending' || summary.paymentStatus === 'partially_paid' || summary.amountDue > 0) && (
                <div className="mb-6">
                  <button
                    onClick={handleSendReminder}
                    disabled={isSendingReminder}
                    className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FaEnvelope />
                    {isSendingReminder ? 'Sending...' : 'Send Payment Reminder'}
                  </button>
                </div>
              )}

              {/* Transactions Timeline */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Transaction History</h3>
                {transactions.length === 0 ? (
                  <div className="text-center py-8 bg-gray-50 rounded-lg">
                    <p className="text-gray-500">No transactions found</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {transactions.map((transaction, index) => (
                      <div
                        key={transaction._id || index}
                        className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              {getStatusIcon(transaction.status)}
                              <div>
                                <p className="font-semibold text-gray-900">{transaction.programName || 'N/A'}</p>
                                <p className="text-sm text-gray-600">Invoice: {transaction.invoiceNo}</p>
                              </div>
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3 text-sm">
                              <div className="flex items-center text-gray-600">
                                <FaCalendarAlt className="mr-2 text-gray-400" />
                                {transaction.date || 'N/A'}
                              </div>
                              <div className="flex items-center text-gray-600">
                                <FaCreditCard className="mr-2 text-gray-400" />
                                {transaction.paymentMethod || 'N/A'}
                              </div>
                              <div className="flex items-center font-semibold text-gray-900">
                                {formatCurrency(transaction.amount || 0)}
                              </div>
                              <div>
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPaymentStatusColor(transaction.status)}`}>
                                  {formatPaymentStatus(transaction.status)}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Reminder History */}
              {reminderHistory.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Reminder History</h3>
                  <div className="space-y-2">
                    {reminderHistory.map((reminder, index) => (
                      <div
                        key={index}
                        className="border border-gray-200 rounded-lg p-3 bg-gray-50"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <FaEnvelope className={`text-sm ${reminder.sent ? 'text-green-600' : 'text-gray-400'}`} />
                            <span className="text-sm text-gray-700">{reminder.message || 'Payment reminder'}</span>
                          </div>
                          <div className="text-xs text-gray-500">
                            {formatDateLocale(reminder.date)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-4 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default PaymentHistoryModal;

